import { LoadingOutlined, PaperClipOutlined } from '@ant-design/icons';
import { message } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAiRuntime } from '../runtime';
import {
  ChatHistoryDetailVO,
  DocumentRef,
  EntityAction,
  FileItem,
  ModuleSuggestionsVO,
  PageContext,
  ProjectVO,
  QuestionAnswerPayload,
  QuestionData,
  UseSkillItem,
  _chatHistoryDetail,
  _chatInterrupt,
  _projectDelete,
  _projectList,
  _uploadFile
} from '../services/aiAssistant';
import { redirectToOidcAuthorize } from '../utils/aiAssistantOidc';
import { ChatTurnController, mergeThinkingTools, runChatTurn, TURN_FAIL_TEXT } from './chatTurn';
import { useAIContextStore } from './contextStore';
import EmptyState from './EmptyState';
import Message from './Message';
import { ChatMsg, ChatMsgThinking, flattenConversations } from './messageMapper';
import ModelPicker from './ModelPicker';
import { isSendKey, readSendMode, SendModeSelector, SEND_MODE_PLACEHOLDER, type SendMode } from './sendMode';
import SessionHeader from './SessionHeader';
import SessionHistoryPanel, { HistoryItem } from './SessionHistoryPanel';
import ShareDialog from './ShareDialog';
import ShareSelectionBar from './ShareSelectionBar';
import SkillChipBar from './SkillChipBar';
import SkillPopover from './SkillPopover';
import SkillsDialog from './SkillsDialog';
import SkillToolButton from './SkillToolButton';
import TokenTag, { MessageTokens } from './TokenTag';
import { clearActiveTurn, clearTurnCursor, readActiveTurn, saveActiveTurn, saveTurnCursor } from './turnPersist';
import { useChatToolbarConfig } from './useChatToolbarConfig';

interface SessionStats {
  totalInput?: number;
  totalComplete?: number;
  totalCache?: number;
  totalTokenCount?: number;
}

interface SessionView {
  id: string;
  projectId: string;
  title: string;
  messages: ChatMsg[];
  loaded: boolean;
  source: 'local-new' | 'remote';
  lastActive: number;
  stats?: SessionStats;
}

interface Props {
  open: boolean;
  onClose: () => void;
  suggestions: ModuleSuggestionsVO | null;
  /** OIDC 授权回来后自动重发的消息（仅消费一次） */
  autoSend?: { message: string; files?: FileItem[]; projectId?: string } | null;
  onAutoSendConsumed?: () => void;
}

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const TITLE_MAX = 15;
const computeLocalTitle = (q: string) => q.slice(0, TITLE_MAX) + (q.length > TITLE_MAX ? '…' : '');

const newLocalSession = (): SessionView => ({
  id: uid(),
  projectId: '',
  title: '新会话',
  messages: [],
  loaded: true,
  source: 'local-new',
  lastActive: Date.now()
});

const mapDetailStats = (stats?: ChatHistoryDetailVO['stats']): SessionStats | undefined => {
  if (!stats) return undefined;
  const { totalInputTokenCnt, totalCompleteTokenCnt, totalCacheTokenCnt, totalTokenCount } = stats;
  const isMissing = (v: number | null | undefined) => v === null || v === undefined;
  if (
    isMissing(totalInputTokenCnt) &&
    isMissing(totalCompleteTokenCnt) &&
    isMissing(totalCacheTokenCnt) &&
    isMissing(totalTokenCount)
  ) {
    return undefined;
  }
  return {
    totalInput: totalInputTokenCnt ?? undefined,
    totalComplete: totalCompleteTokenCnt ?? undefined,
    totalCache: totalCacheTokenCnt ?? undefined,
    totalTokenCount: totalTokenCount ?? undefined
  };
};

/** 把新一轮 tokens 累加到 session 维度 stats */
const accumulateSessionStats = (prev: SessionStats | undefined, tokens: MessageTokens): SessionStats => {
  const base = {
    totalInput: prev?.totalInput ?? 0,
    totalComplete: prev?.totalComplete ?? 0,
    totalCache: prev?.totalCache ?? 0,
    totalTokenCount: prev?.totalTokenCount ?? 0
  };
  return {
    totalInput: base.totalInput + (tokens.input ?? 0),
    totalComplete: base.totalComplete + (tokens.complete ?? 0),
    totalCache: base.totalCache + (tokens.cache ?? 0),
    totalTokenCount: base.totalTokenCount + (tokens.total ?? 0)
  };
};

/** 把 projects/list 返回的 ProjectVO 合并到本地 sessions[]：
 *  - 已有 SessionView（按 projectId 匹配）→ 仅刷新 title/lastActive/source
 *  - 不在本地 → append 一个未 loaded 的 remote view */
const mergeSessions = (locals: SessionView[], remotes: ProjectVO[]): SessionView[] => {
  const next: SessionView[] = locals.map((s) => ({ ...s }));
  for (const item of remotes) {
    const remoteProjectId = item.projectId || '';
    if (!remoteProjectId) continue;
    const idx = next.findIndex((s) => s.projectId === remoteProjectId);
    const remoteTitle = item.title || '';
    const remoteLastActive = item.updateTime
      ? new Date(item.updateTime).getTime()
      : item.createTime
      ? new Date(item.createTime).getTime()
      : 0;
    if (idx >= 0) {
      const cur = next[idx];
      next[idx] = {
        ...cur,
        projectId: cur.projectId || remoteProjectId,
        title: remoteTitle || cur.title,
        source: 'remote',
        lastActive: Math.max(cur.lastActive, remoteLastActive)
      };
    } else {
      next.push({
        id: uid(),
        projectId: remoteProjectId,
        title: remoteTitle || '新会话',
        messages: [],
        loaded: false,
        source: 'remote',
        lastActive: remoteLastActive || Date.now()
      });
    }
  }
  // 按 lastActive 倒序：local-new 一般 lastActive=Date.now() 排前
  next.sort((a, b) => b.lastActive - a.lastActive);
  return next;
};

/* ─── 上传约束 ─── */
const MAX_FILE_COUNT = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPT_EXTS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.bmp',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.txt',
  '.md'
];
const ACCEPT_ATTR = ACCEPT_EXTS.join(',');

/* ─── 打开期间隐藏页面视口滚动条 ─── */
const DRAWER_OPEN_CLASS = 'ai-drawer-open';
const DRAWER_OPEN_STYLE_ID = 'ai-drawer-open-style';

/**
 * 样式由 core 自己注入，而不是要求各宿主在自己的 global.less 里写一遍。
 *
 * 加 class 的行为在 core，样式却散落在宿主，新接入的应用必漏 ——
 * 实际上只有 ab-test 写了，recommend / widget / smartoperation 都没有，
 * 面板打开时页面滚动条一直没被隐藏，和面板内滚动条紧挨着两条，很容易拖错那条。
 *
 * `::-webkit-scrollbar` 没法走 inline style，所以用一个单例 <style>。
 */
const ensureDrawerOpenStyle = () => {
  if (document.getElementById(DRAWER_OPEN_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = DRAWER_OPEN_STYLE_ID;
  // 仅隐藏滚动条，overflow 不动：页面仍可正常滚动/操作
  el.textContent =
    `html.${DRAWER_OPEN_CLASS}{scrollbar-width:none;-ms-overflow-style:none}` +
    `html.${DRAWER_OPEN_CLASS}::-webkit-scrollbar{width:0!important;height:0!important;background:transparent}`;
  document.head.appendChild(el);
};

export default function AIDrawer({ open, onClose, suggestions, autoSend, onAutoSendConsumed }: Props) {
  const runtime = getAiRuntime();
  const ctx = useAIContextStore((s) => s.ctx);

  const [sessions, setSessions] = useState<SessionView[]>(() => [newLocalSession()]);
  const [viewingSessionId, setViewingSessionId] = useState<string>(() => '');
  const [input, setInput] = useState('');
  const [inputHeight, setInputHeight] = useState(96);
  const [drawerWidth, setDrawerWidth] = useState(460);
  const [isReplying, setIsReplying] = useState(false);
  const [processText, setProcessText] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<FileItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [historyLoadingId, setHistoryLoadingId] = useState<string | null>(null);
  const [sendInflight, setSendInflight] = useState(false);

  // 分享多选态
  const [shareMode, setShareMode] = useState<null | 'loading' | 'selecting'>(null);
  const [selectedTurnIds, setSelectedTurnIds] = useState<Set<string>>(new Set());
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sendMode, setSendMode] = useState<SendMode>(readSendMode);
  const [sendModeMenuOpen, setSendModeMenuOpen] = useState(false);

  /** 当前进行中的对话轮次（内部含 SSE 续推与轮询兜底）；stopTurn() 后置 null */
  const turnRef = useRef<ChatTurnController | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autoSentRef = useRef(false);
  /**
   * 流式回复是否跟随滚动。用 ref 而非「内容变化时现量贴底」：
   * effect 里读到的 scrollTop 是旧值、scrollHeight 是新值，单帧涌入超过阈值高度的内容
   * （markdown 渲染很容易）就会被判成「用户已上滑」而中断跟随。
   * 这里改为由用户的滚动行为单独维护开关，内容增长时无条件跟随。
   */
  const autoScrollRef = useRef(true);
  /** resume 正在尝试中：防重入。关闭抽屉时重置，使每次重新打开都能再尝试 */
  const resumeInflightRef = useRef(false);
  /** 已判定不可续推的轮次 uuid：避免对同一个终态轮次反复拉 history */
  const unresumableUuidRef = useRef<string>('');
  /**
   * accept 帧未到达前的回滚钩子。此时后端还没给出 uuid，turnPersist 里也没有游标，
   * 这一轮不可续推，关闭抽屉就得把 user + ai 占位撤掉并把输入回填，否则重开会留一条永远转圈的消息。
   * accept 到达 / 降级 / 落终态后置 null（那之后由 resume 或 onSettled 接管收尾）。
   */
  const preAcceptRollbackRef = useRef<(() => void) | null>(null);
  const viewingSessionIdRef = useRef<string>('');
  /** sessions 最新快照：异步回调里读，避免闭包拿到过期的会话列表 */
  const sessionsRef = useRef<SessionView[]>([]);
  const oidcRedirectedRef = useRef(false);
  /** askQuestion 提交竞态锁：防止快速连续提交 */
  const questionSubmittingRef = useRef(false);
  const skillAnchorRef = useRef<HTMLDivElement>(null);

  /* ─── 首次打开兜底：sessions 还没建好的 viewing，落到第一个 ─── */
  useEffect(() => {
    if (!viewingSessionId && sessions.length > 0) {
      setViewingSessionId(sessions[0].id);
    }
  }, [viewingSessionId, sessions]);

  // 保持 ref 同步
  useEffect(() => {
    viewingSessionIdRef.current = viewingSessionId;
  }, [viewingSessionId]);

  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  // 抽屉打开 → 拉模型清单 + 当前配置 + Skill 分类；关闭时重置（不缓存）
  const {
    providers,
    currentModelId,
    configLoaded,
    selectedSkillIds,
    sharedSkillIds,
    enabledSkillIds,
    skillDetailMap,
    skillCategories,
    popoverOpen,
    setPopoverOpen,
    dialogOpen,
    setDialogOpen,
    skillSubmitting,
    handleModelChange,
    handleRemoveSkill,
    handleToggleEnabledSkill,
    handleConfirmSkills,
    skillLoaders,
    buildUseSkills,
    clearEnabledSkills
  } = useChatToolbarConfig({
    active: open,
    onOidcRequired: () => triggerOidcRedirect()
  });

  // 关闭抽屉释放 OIDC 跳转锁，下次打开可重新触发
  useEffect(() => {
    if (!open) {
      oidcRedirectedRef.current = false;
    }
  }, [open]);

  const triggerOidcRedirect = () => {
    if (oidcRedirectedRef.current) return;
    oidcRedirectedRef.current = true;
    stopTurn();
    redirectToOidcAuthorize({
      message: '',
      returnPath: window.location.pathname,
      floating: true
    });
  };

  const viewing = useMemo(
    () => sessions.find((s) => s.id === viewingSessionId) || sessions[0],
    [sessions, viewingSessionId]
  );

  /** 判定「贴底」的容差：行高级别的余量，避免小数像素导致误判 */
  const SCROLL_BUFFER = 24;

  /** 最近一次程序化写入的 scrollTop，用于在 scroll 事件里排除自己触发的那一次 */
  const programmaticTopRef = useRef(-1);
  const scrollRafRef = useRef(0);

  const scrollToBottom = () => {
    const el = listRef.current;
    if (!el) return;
    const top = Math.max(0, el.scrollHeight - el.clientHeight);
    programmaticTopRef.current = top;
    el.scrollTop = top;
  };

  /**
   * 置底写入按帧合并。流式分片一帧内可能到达多次，
   * 每次写 scrollTop 都会强制同步布局（此时 DOM 刚被 React 改脏），是滚动卡顿的主要来源。
   */
  const scheduleScrollToBottom = () => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = 0;
      scrollToBottom();
    });
  };

  /** 开启跟随并置底：新一轮对话 / 抽屉打开时调用 */
  const armAutoScroll = () => {
    autoScrollRef.current = true;
    scheduleScrollToBottom();
  };

  /**
   * 用户滚动 → 重算跟随开关。上滑离开底部即停止跟随，手动滚回底部则恢复。
   *
   * 必须排除程序化写入触发的那次事件：scroll 事件是异步派发的，置底之后内容若又长高
   * （追加新消息、markdown 渲染完成），事件到达时量出的 scrollHeight 已是新值而 scrollTop
   * 仍是旧的底部，差值超过容差就会被误判成「用户上滑」而关掉跟随 —— 这正是
   * 「上滑看历史后重新发起对话不再置底」以及流式输出时拖滚动条打架的原因。
   */
  const handleListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    if (Math.abs(el.scrollTop - programmaticTopRef.current) <= 1) return;
    programmaticTopRef.current = -1;
    autoScrollRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_BUFFER;
  };

  // 流式增量 / 思考进度 → 跟随到底（用户上滑后不再跟随，由新一轮对话重新开启）
  useEffect(() => {
    if (autoScrollRef.current) scheduleScrollToBottom();
  }, [viewing?.messages, processText]);

  // 抽屉打开 → 定位到最新消息并重新开启跟随。
  // 关闭时内容整棵 DOM 被卸载（return null），重新挂载后 scrollTop 为 0，
  // 双 rAF 等 Drawer 展开动画首帧后再量 scrollHeight，否则量到的是未布局完的高度
  useEffect(() => {
    if (!open) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(armAutoScroll);
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open]);

  // 抽屉打开时隐藏页面视口滚动条（仅视觉隐藏，页面仍可正常滚动/操作），
  // 避免与面板内滚动条同时出现两条滚动条
  useEffect(() => {
    if (!open) return;
    ensureDrawerOpenStyle();
    document.documentElement.classList.add(DRAWER_OPEN_CLASS);
    return () => {
      document.documentElement.classList.remove(DRAWER_OPEN_CLASS);
    };
  }, [open]);

  // 抽屉打开聚焦输入框；关闭时停止轮询/流并重置处理态
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 200);
    } else {
      stopTurn();
      // accept 未到达就关闭：本轮拿不到 uuid，resumeStream 无从续推，整轮撤回
      preAcceptRollbackRef.current?.();
      preAcceptRollbackRef.current = null;
      // abort 不走 onSettled，发送锁只能在这里释放，否则「新建对话」会被 sendInflight 永久挡住
      setSendInflight(false);
      setIsReplying(false);
      setProcessText('');
      setHistoryPanelOpen(false);
      setShareMode(null);
      setSelectedTurnIds(new Set());
      setShareDialogOpen(false);
    }
  }, [open]);

  // 组件卸载：兜底清理轮询定时器 + SSE 连接 + 待执行的置底帧
  useEffect(() => {
    return () => {
      stopTurn();
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  /* ─── 抽屉打开 → 拉一次 projects/list ─── */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await _projectList();
        if (cancelled) return;
        setSessions((prev) => mergeSessions(prev, list || []));
      } catch {
        // 不阻塞抽屉
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // OIDC 授权回来：自动恢复上次输入并重发（只消费一次）
  useEffect(() => {
    if (!open || !autoSend || autoSentRef.current) return;
    autoSentRef.current = true;
    const targetProjectId = autoSend.projectId || '';

    // 解析目标 session：projectId 匹配 > 空 local-new viewing > 新建
    let resolvedId: string | null = null;
    if (targetProjectId) {
      const found = sessions.find((s) => s.projectId === targetProjectId);
      if (found) resolvedId = found.id;
    } else {
      const cur = sessions.find((s) => s.id === viewingSessionIdRef.current);
      if (cur && cur.source === 'local-new' && cur.messages.length === 0) {
        resolvedId = cur.id;
      }
    }
    if (!resolvedId) {
      const fresh: SessionView = targetProjectId
        ? { ...newLocalSession(), projectId: targetProjectId }
        : newLocalSession();
      resolvedId = fresh.id;
      setSessions((prev) => [fresh, ...prev]);
    }
    const finalId = resolvedId;
    setTimeout(() => {
      setViewingSessionId(finalId);
      handleSend(autoSend.message, autoSend.files, autoSend.projectId, finalId);
      onAutoSendConsumed?.();
    }, 0);
  }, [open, autoSend]);

  /** 结束当前轮次：断流 + 停轮询，且不再触发回调 */
  const stopTurn = () => {
    turnRef.current?.abort();
    turnRef.current = null;
  };

  /**
   * 消费 pollWithStatus 消息里的 entityAction 信号：
   * - 目前仅识别 action === 'refresh'；其它值静默忽略；
   * - 具体实体的刷新/跳转策略由各应用通过 `setAiRuntime({ onEntityRefresh })` 注入；
   * - 不弹任何 message / modal，反馈依赖 AI type=3 回复文案。
   */
  const handleEntityRefresh = (action: EntityAction) => {
    if (action.action !== 'refresh') return;
    runtime.onEntityRefresh?.(action);
  };

  const buildContext = (): PageContext | undefined => {
    const latest = useAIContextStore.getState().ctx;
    if (!latest.moduleId) return undefined;
    return {
      moduleId: latest.moduleId,
      url: latest.url || window.location.href,
      pageTitle: latest.pageTitle,
      breadcrumb: latest.breadcrumb,
      filters: latest.filters,
      recentActions: latest.recentActions,
      entityType: latest.entityType,
      entityId: latest.entityId,
      gameId: latest.gameId ?? runtime.getGameId?.()
    };
  };

  /** 发送消息：closure 内捕获 targetSessionId，SSE 帧按 sessionId 维度写回 */
  /** askQuestion：把问题数据挂到指定会话的指定 AI 消息上 */
  const attachQuestionData = (sessionId: string, msgId: string, questionData: QuestionData) => {
    setSessions((prev) =>
      prev.map((s) =>
        s.id === sessionId
          ? { ...s, messages: s.messages.map((m) => (m.id === msgId ? { ...m, questionData } : m)) }
          : s
      )
    );
  };

  /** askQuestion：提交作答 —— 写只读快照、追加 ai 占位、以空 message + questionAnswers 发起新流 */
  const handleSubmitAnswer = (sessionId: string, msgId: string, payload: QuestionAnswerPayload) => {
    if (questionSubmittingRef.current || isReplying || sendInflight) return;
    questionSubmittingRef.current = true;

    stopTurn();

    const aiTempId = `ai_pending_${uid()}`;
    const session = sessions.find((s) => s.id === sessionId);
    const initialProjectId = session?.projectId || '';

    setSessions((prev) =>
      prev.map((s) =>
        s.id === sessionId
          ? {
              ...s,
              messages: [
                ...s.messages.map((m) => (m.id === msgId ? { ...m, questionAnswers: payload } : m)),
                {
                  id: aiTempId,
                  turnId: '',
                  role: 'ai' as const,
                  content: '',
                  thinking: { text: '', tools: [], status: 'in-progress' as const, expanded: true },
                  createTime: new Date().toISOString()
                }
              ],
              lastActive: Date.now()
            }
          : s
      )
    );
    setIsReplying(true);
    setProcessText('思考中...');
    setSendInflight(true);

    startStream({
      targetSessionId: sessionId,
      trimmed: '',
      sentFiles: undefined,
      userMsgId: '',
      aiTempId,
      initialProjectId,
      useSkillsPayload: undefined,
      questionAnswers: payload
    });
    questionSubmittingRef.current = false;
  };

  const handleSend = async (
    text: string,
    overrideFiles?: FileItem[],
    projectIdOverride?: string,
    targetSessionIdOverride?: string
  ) => {
    const trimmed = text.trim();
    if (!trimmed || isReplying || uploading || sendInflight) return;

    const targetSessionId = targetSessionIdOverride || viewingSessionIdRef.current;
    if (!targetSessionId) return;

    // 起新一轮前打断上一轮流 & 轮询（防御性；正常路径不会重叠）
    stopTurn();

    const userMsgId = uid();
    const aiTempId = `ai_pending_${uid()}`;
    const userCreateTime = new Date().toISOString();
    const baseFiles = overrideFiles ?? uploadedFiles;
    const sentFiles = baseFiles.length ? [...baseFiles] : undefined;

    // 立即 push user msg + 临时 ai placeholder（含 in-progress thinking 卡片）
    setSessions((prev) =>
      prev.map((s) =>
        s.id === targetSessionId
          ? {
              ...s,
              messages: [
                ...s.messages,
                {
                  id: userMsgId,
                  turnId: '',
                  role: 'user',
                  content: trimmed,
                  files: sentFiles,
                  createTime: userCreateTime
                },
                {
                  id: aiTempId,
                  turnId: '',
                  role: 'ai',
                  content: '',
                  thinking: { text: '', tools: [], status: 'in-progress', expanded: true },
                  createTime: new Date().toISOString()
                }
              ],
              lastActive: Date.now()
            }
          : s
      )
    );
    setInput('');
    setUploadedFiles([]);
    setIsReplying(true);
    setPopoverOpen(false);
    setProcessText('思考中...');
    setSendInflight(true);

    // 闭包内固定的目标 session 的 projectId
    const inflightSession = sessions.find((s) => s.id === targetSessionId);
    const initialProjectId = projectIdOverride ?? (inflightSession?.projectId || '');

    const useSkillsPayload = buildUseSkills();

    startStream({
      targetSessionId,
      trimmed,
      sentFiles,
      userMsgId,
      aiTempId,
      initialProjectId,
      useSkillsPayload
    });
  };

  /**
   * 建立 SSE 流式对话。handlers 内所有 setSessions 均使用闭包 targetSessionId，
   * 保证切会话后回包仍写回原会话；抽象共享 send / resume 两阶段的一套 handlers。
   *
   * 传 `resume` 时跳过 sendStream，直接以 `resumeStream` + `Last-Event-ID` 从游标续推
   * （刷新后恢复进行中轮次），降级链路与新发送一致。
   */
  const startStream = ({
    targetSessionId,
    trimmed,
    sentFiles,
    userMsgId,
    aiTempId,
    initialProjectId,
    useSkillsPayload,
    questionAnswers,
    resume
  }: {
    targetSessionId: string;
    trimmed: string;
    sentFiles: FileItem[] | undefined;
    userMsgId: string;
    aiTempId: string;
    initialProjectId: string;
    useSkillsPayload: UseSkillItem[] | undefined;
    questionAnswers?: QuestionAnswerPayload;
    resume?: { uuid: string; projectId: string; lastEventId?: string };
  }) => {
    // 新一轮对话（含续推）重新开启跟随：上一轮里用户可能已上滑停掉了
    armAutoScroll();
    // 已协商的 projectId：由 onAccept 更新
    let aiMsgId = aiTempId;
    let acceptedProjectId = initialProjectId;

    /** ai 消息 patch：可同时更新 thinking 与 content/documents/tokens */
    const patchAiMessage = (
      thinkingPatch: Partial<ChatMsgThinking> | null,
      contentPatch?: { content?: string; documents?: DocumentRef[]; tokens?: MessageTokens }
    ) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== targetSessionId) return s;
          return {
            ...s,
            messages: s.messages.map((m) => {
              if (m.id !== aiMsgId) return m;
              let nextThinking = m.thinking;
              if (thinkingPatch && m.thinking) {
                nextThinking = { ...m.thinking, ...thinkingPatch };
                if (thinkingPatch.tools) nextThinking.tools = mergeThinkingTools(m.thinking.tools, thinkingPatch.tools);
              }
              return {
                ...m,
                thinking: nextThinking,
                ...(contentPatch?.content !== undefined ? { content: contentPatch.content } : {}),
                ...(contentPatch?.documents !== undefined ? { documents: contentPatch.documents } : {}),
                ...(contentPatch?.tokens !== undefined ? { tokens: contentPatch.tokens } : {})
              };
            })
          };
        })
      );
    };

    // resume 的消息来自 history 回显，删不得；新发送的一轮在 accept 之前都可以整体撤回
    preAcceptRollbackRef.current = resume
      ? null
      : () => {
          setSessions((prev) =>
            prev.map((s) =>
              s.id === targetSessionId
                ? { ...s, messages: s.messages.filter((m) => m.id !== userMsgId && m.id !== aiMsgId) }
                : s
            )
          );
          // 用 updater 读当前值：只在用户没重新输入时回填，避免覆盖新草稿
          setInput((cur) => cur || trimmed);
          setUploadedFiles((cur) => (cur.length ? cur : sentFiles ?? []));
        };

    turnRef.current = runChatTurn(
      resume
        ? { resume, initialProjectId }
        : {
            request: {
              message: trimmed,
              files: sentFiles,
              useSkills: useSkillsPayload,
              context: buildContext(),
              questionAnswers
            },
            initialProjectId
          },
      {
        onCursor(cursor) {
          // 节流落盘，刷新后据此 resumeStream 续推
          saveTurnCursor(cursor);
        },
        onAccept(data) {
          acceptedProjectId = data.projectId;
          const newAiMsgId = `ai_${data.uuid}`;
          // 关键：先捕获旧值到 const，setSessions 的 functional updater 会延迟到 flush 时执行，
          // 那时闭包里的 aiMsgId 已被下面的赋值改写，用 outer let 会命中不到 pending 占位
          const prevAiMsgId = aiMsgId;
          const newTurnId = data.uuid;
          aiMsgId = newAiMsgId;
          clearEnabledSkills();
          setSessions((prev) =>
            prev.map((s) => {
              if (s.id !== targetSessionId) return s;
              const isLocalFirst = s.source === 'local-new';
              const backfilled = s.messages.map((m) => {
                if (m.id === prevAiMsgId) return { ...m, id: newAiMsgId, turnId: newTurnId };
                if (m.turnId === '') return { ...m, turnId: newTurnId };
                return m;
              });
              return {
                ...s,
                projectId: data.projectId,
                source: 'remote',
                title: isLocalFirst ? computeLocalTitle(trimmed) : s.title,
                lastActive: Date.now(),
                messages: backfilled
              };
            })
          );
          // 会话上下文落盘：刷新后据 projectId 回显历史、据 uuid 判断是否需要续推
          saveActiveTurn({
            projectId: data.projectId,
            uuid: data.uuid,
            ...(trimmed ? { title: computeLocalTitle(trimmed) } : {})
          });
          setSendInflight(false);
          // accept 已到达，后端有 uuid 了 → 本轮可续推，关闭抽屉不再撤回
          preAcceptRollbackRef.current = null;
        },
        onTextChunk(chunk) {
          setSessions((prev) =>
            prev.map((s) => {
              if (s.id !== targetSessionId) return s;
              return {
                ...s,
                messages: s.messages.map((m) => (m.id === aiMsgId ? { ...m, content: (m.content || '') + chunk } : m))
              };
            })
          );
        },
        onThinkingChunk(chunk) {
          setSessions((prev) =>
            prev.map((s) => {
              if (s.id !== targetSessionId) return s;
              return {
                ...s,
                messages: s.messages.map((m) => {
                  if (m.id !== aiMsgId) return m;
                  const prevText = m.thinking?.text || '';
                  const nextThinking = m.thinking
                    ? { ...m.thinking, text: prevText + chunk }
                    : { text: chunk, tools: [], status: 'in-progress' as const, expanded: true };
                  return { ...m, thinking: nextThinking };
                })
              };
            })
          );
          if (viewingSessionIdRef.current === targetSessionId) {
            // 展示末段，避免 processText 过长（末尾最多 80 字）
            setProcessText((prev) => {
              const merged = (prev && prev !== '思考中...' ? prev : '') + chunk;
              return merged.length > 80 ? merged.slice(-80) : merged;
            });
          }
        },
        // 去重协作：reasoning 帧的 text 与 text_chunk 累积正文重复，chatTurn 已丢弃，只回调结构化 tools
        onTools: (tools) => patchAiMessage({ tools }),
        onProgress({ text, tools }) {
          patchAiMessage({ text, tools });
          if (viewingSessionIdRef.current === targetSessionId) setProcessText(text || '正在处理...');
        },
        onEntityAction: handleEntityRefresh,
        onPhaseChange(nextPhase) {
          // 降级到 resume / poll 时释放发送锁，避免按钮长期禁用
          if (nextPhase !== 'send') setSendInflight(false);
        },
        onOidcRequired() {
          // OIDC：回滚 user + ai 占位，恢复 input，走跳转
          turnRef.current = null;
          preAcceptRollbackRef.current = null;
          clearTurnCursor();
          // resume 场景无原始输入，且消息来自 history 回显，回滚会误删历史 → 仅重置处理态
          if (!resume) {
            setSessions((prev) =>
              prev.map((s) =>
                s.id === targetSessionId
                  ? {
                      ...s,
                      messages: s.messages.filter((m) => m.id !== userMsgId && m.id !== aiMsgId)
                    }
                  : s
              )
            );
            setInput(trimmed);
            setUploadedFiles(sentFiles ?? []);
          }
          setIsReplying(false);
          setProcessText('');
          setSendInflight(false);
          redirectToOidcAuthorize({
            message: trimmed,
            files: sentFiles,
            projectId: acceptedProjectId || initialProjectId || undefined,
            returnPath: window.location.pathname,
            floating: true
          });
        },
        onSettled(result) {
          turnRef.current = null;
          // 已落终态（含 accept 未到就失败的场景）：失败文案已写进消息，关闭抽屉不该再撤回
          preAcceptRollbackRef.current = null;
          // 轮次落终态：清掉续推游标，保留 projectId/title 供刷新后回显
          clearTurnCursor();
          const isFailed = result.status === 'FAILED';
          const finalTokens = result.tokens;
          patchAiMessage(
            { status: isFailed ? 'failed' : 'done', expanded: false },
            {
              // 成功但快照无正文：保留 text_chunk 已累积的内容
              ...(isFailed
                ? { content: TURN_FAIL_TEXT }
                : result.content !== undefined
                ? { content: result.content }
                : {}),
              documents: result.documents,
              tokens: finalTokens
            }
          );
          // askQuestion：终态快照里含 questionId 的消息 → 挂到本轮 AI 消息
          if (result.questionData) attachQuestionData(targetSessionId, aiMsgId, result.questionData);
          setSessions((prev) =>
            prev.map((s) =>
              s.id === targetSessionId
                ? {
                    ...s,
                    lastActive: Date.now(),
                    stats: finalTokens ? accumulateSessionStats(s.stats, finalTokens) : s.stats
                  }
                : s
            )
          );
          if (viewingSessionIdRef.current === targetSessionId) {
            setIsReplying(false);
            setProcessText('');
          }
          setSendInflight(false);
        }
      }
    );
  };

  /* ─── 抽屉打开 → 续推未完成的轮次（resumeStream 重接） ───
   *
   * 两个触发场景，都会断开本地 SSE 但后端仍在跑：
   *   - 页面刷新：销毁 runChatTurn 闭包内的 uuid / lastEventId
   *   - 关闭抽屉：open=false 的 effect 调 stopTurn() abort 掉连接
   * 用 turnPersist 落盘的 (projectId, uuid, lastEventId) 恢复：
   *   1. 拉 /chat/history/detail 回显该会话；
   *   2. 该 uuid 仍 IN_PROGRESS → resumeStream 带 Last-Event-ID 从游标续推；
   *   3. 已终态 / 轮次不存在 → 清游标并记入 unresumable，不重接。
   * 游标失效时由 chatTurn 内部降级为 pollWithStatus 轮询。
   *
   * 注意：AIDrawer 关闭时只是 `return null`，组件不卸载，ref 不会自动重置，
   * 所以这里用「关闭即复位」的 inflight 锁，而不是一次性 latch。
   */
  useEffect(() => {
    if (!open) {
      resumeInflightRef.current = false;
      return;
    }
    if (resumeInflightRef.current) return;
    // OIDC 自动重发优先，避免两条流并发
    if (autoSend) return;
    // 本地已有进行中的轮次（正常发送流）→ 不重接
    if (turnRef.current) return;
    const persisted = readActiveTurn();
    if (!persisted?.uuid || !persisted.projectId) return;
    if (persisted.uuid === unresumableUuidRef.current) return;

    resumeInflightRef.current = true;
    const { projectId, uuid, lastEventId } = persisted;
    let cancelled = false;

    (async () => {
      try {
        const detail: ChatHistoryDetailVO = await _chatHistoryDetail({ projectId });
        if (cancelled) return;
        const conv = (detail?.conversations || []).find((c) => c.uuid === uuid);
        if (!conv || conv.status !== 'IN_PROGRESS') {
          unresumableUuidRef.current = uuid;
          resumeInflightRef.current = false;
          clearTurnCursor();
          return;
        }

        const aiMsgId = `ai_${uuid}`;
        const mapped = flattenConversations(detail?.conversations || []);
        // 续推目标消息：history 已有则强制回到 in-progress，否则补一个占位
        const idx = mapped.findIndex((m) => m.id === aiMsgId);
        const inProgressThinking: ChatMsgThinking = { text: '', tools: [], status: 'in-progress', expanded: true };
        let messages: ChatMsg[];
        if (idx < 0) {
          messages = [
            ...mapped,
            {
              id: aiMsgId,
              turnId: uuid,
              role: 'ai',
              content: '',
              thinking: inProgressThinking,
              createTime: new Date().toISOString()
            }
          ];
        } else {
          messages = [...mapped];
          const cur = messages[idx];
          messages[idx] = {
            ...cur,
            thinking: cur.thinking ? { ...cur.thinking, status: 'in-progress', expanded: true } : inProgressThinking
          };
        }

        const remoteStats = mapDetailStats(detail?.stats);
        const existing = sessionsRef.current.find((s) => s.projectId === projectId);
        const sessionId = existing ? existing.id : uid();
        setSessions((prev) => {
          const base: SessionView = {
            id: sessionId,
            projectId,
            title: detail?.title || persisted.title || '新会话',
            messages,
            loaded: true,
            source: 'remote',
            lastActive: Date.now(),
            stats: remoteStats
          };
          // projects/list 可能并发插入同 projectId 的视图 → 去重，只保留本次目标
          const rest = prev.filter((s) => s.id === sessionId || s.projectId !== projectId);
          return rest.some((s) => s.id === sessionId)
            ? rest.map((s) => (s.id === sessionId ? { ...s, ...base, title: detail?.title || persisted.title || s.title } : s))
            : [base, ...rest];
        });
        setViewingSessionId(sessionId);
        setIsReplying(true);
        setProcessText('正在恢复...');

        stopTurn();
        startStream({
          targetSessionId: sessionId,
          trimmed: '',
          sentFiles: undefined,
          userMsgId: '',
          aiTempId: aiMsgId,
          initialProjectId: projectId,
          useSkillsPayload: undefined,
          resume: { uuid, projectId, lastEventId: lastEventId || undefined }
        });
      } catch {
        // history 拉取失败：放弃重接。不记 unresumable，下次打开可再试
        if (!cancelled) {
          resumeInflightRef.current = false;
          clearTurnCursor();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, autoSend]);

  const handleInterrupt = async () => {
    stopTurn();
    // 用户主动打断：下面已把占位标成 failed，关闭抽屉不该再把这条消息撤回
    preAcceptRollbackRef.current = null;
    clearTurnCursor();
    setIsReplying(false);
    setProcessText('');
    setSendInflight(false);
    // 中断当前 viewing 会话进行中的 thinking（挂在 ai 消息上）
    const currentSessionId = viewingSessionIdRef.current;
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== currentSessionId) return s;
        return {
          ...s,
          messages: s.messages.map((m) =>
            m.role === 'ai' && m.thinking && m.thinking.status === 'in-progress'
              ? { ...m, thinking: { ...m.thinking, status: 'failed', expanded: false } }
              : m
          )
        };
      })
    );
    const pid = viewing?.projectId;
    if (pid) {
      try {
        await _chatInterrupt({ projectId: pid });
      } catch {
        // 错误提示由 request 拦截器统一处理
      }
    }
  };

  const handleNewSession = async () => {
    if (shareMode === 'selecting' || shareMode === 'loading') return;
    if (uploading) {
      message.warning('请等待文件上传完成');
      return;
    }
    if (sendInflight) return; // 等待 _chatSend resolve

    if (isReplying) {
      await handleInterrupt();
    }

    const fresh = newLocalSession();
    // 明确要空白会话：整条记录作废，避免刷新后又恢复到上一个项目
    clearActiveTurn();
    setSessions((prev) => {
      const current = prev.find((s) => s.id === viewingSessionIdRef.current);
      const next =
        current && current.source === 'local-new' && current.messages.length === 0
          ? prev.filter((s) => s.id !== current.id)
          : prev;
      return [fresh, ...next];
    });
    setViewingSessionId(fresh.id);
    setHistoryPanelOpen(false);
    setShareMode(null);
    setSelectedTurnIds(new Set());
    setShareDialogOpen(false);
  };

  const handleSwitchSession = async (targetId: string) => {
    if (shareMode === 'selecting' || shareMode === 'loading') return;
    if (targetId === viewingSessionIdRef.current) {
      setHistoryPanelOpen(false);
      return;
    }
    if (sendInflight) {
      message.warning('消息正在发送中，请稍后切换');
      return;
    }
    if (uploading) {
      message.warning('请等待文件上传完成');
      return;
    }
    if (isReplying) {
      await handleInterrupt();
    }
    setViewingSessionId(targetId);
    setHistoryPanelOpen(false);
    setShareMode(null);
    setSelectedTurnIds(new Set());
    setShareDialogOpen(false);

    const target = sessions.find((s) => s.id === targetId);
    if (!target || target.loaded || !target.projectId) return;

    setHistoryLoadingId(targetId);
    try {
      const detail: ChatHistoryDetailVO = await _chatHistoryDetail({ projectId: target.projectId });
      const mapped = flattenConversations(detail?.conversations || []);
      const remoteTitle = detail?.title || '';
      const remoteLastActive = detail?.updateTime
        ? new Date(detail.updateTime).getTime()
        : detail?.createTime
        ? new Date(detail.createTime).getTime()
        : 0;
      const remoteStats = mapDetailStats(detail?.stats);
      setSessions((prev) =>
        prev.map((s) =>
          s.id === targetId
            ? {
                ...s,
                messages: mapped,
                loaded: true,
                title: remoteTitle || s.title,
                lastActive: remoteLastActive ? Math.max(s.lastActive, remoteLastActive) : s.lastActive,
                stats: remoteStats
              }
            : s
        )
      );
    } catch {
      // 错误提示由 request 拦截器统一处理
    } finally {
      setHistoryLoadingId((id) => (id === targetId ? null : id));
    }
  };

  const handleDeleteSession = async (targetId: string) => {
    const target = sessions.find((s) => s.id === targetId);
    if (!target) return;
    const isViewing = targetId === viewingSessionIdRef.current;

    if (target.source === 'local-new') {
      // 仅本地移除
      removeSessionWithFallback(targetId, isViewing);
      return;
    }

    if (!target.projectId) {
      message.warning('会话未就绪，无法删除');
      return;
    }

    if (isViewing && isReplying) {
      await handleInterrupt();
    }

    try {
      await _projectDelete({ projectId: target.projectId });
      // 被删项目正是落盘记录的项目 → 作废，避免刷新后重接到已删会话
      if (readActiveTurn()?.projectId === target.projectId) {
        clearActiveTurn();
      }
      removeSessionWithFallback(targetId, isViewing);
    } catch {
      // 错误提示由 request 拦截器统一处理
    }
  };

  const removeSessionWithFallback = (targetId: string, isViewing: boolean) => {
    const next = sessions.filter((s) => s.id !== targetId);
    if (next.length === 0) {
      const fresh = newLocalSession();
      setSessions([fresh]);
      if (isViewing) setViewingSessionId(fresh.id);
    } else {
      setSessions(next);
      if (isViewing) setViewingSessionId(next[0].id);
    }
  };

  /** 当前 viewing 的 turn 索引：有序唯一 turnId 列表 + 每个 turn 是否可分享 */
  const turnSummary = useMemo(() => {
    const msgs = viewing?.messages || [];
    const seen = new Set<string>();
    const turnIds: string[] = [];
    const turnSelectable: Record<string, boolean> = {};
    for (const m of msgs) {
      if (!m.turnId || m.turnId.startsWith('local_fail_')) continue;
      if (!seen.has(m.turnId)) {
        seen.add(m.turnId);
        turnIds.push(m.turnId);
        turnSelectable[m.turnId] = true;
      }
      // 失败 ai 占位且无 content → 该 turn 不可勾选
      if (m.role === 'ai' && m.thinking?.status === 'failed' && !m.content) {
        turnSelectable[m.turnId] = false;
      }
    }
    return { turnIds, turnSelectable };
  }, [viewing?.messages]);

  const enterShareMode = async (seedTurnId: string) => {
    if (!viewing) return;
    if (viewing.source === 'local-new' || !viewing.projectId) {
      message.warning('当前会话尚未保存，无法分享');
      return;
    }
    if (isReplying) {
      message.warning('AI 正在回复中，请稍后再分享');
      return;
    }
    setSelectedTurnIds(new Set([seedTurnId]));
    setShareMode('loading');
    try {
      const detail: ChatHistoryDetailVO = await _chatHistoryDetail({ projectId: viewing.projectId });
      const mapped = flattenConversations(detail?.conversations || []);
      const remoteStats = mapDetailStats(detail?.stats);
      const sessionId = viewing.id;
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                messages: mapped,
                loaded: true,
                stats: remoteStats ?? s.stats
              }
            : s
        )
      );
      // 同步后用最新 messages 中合法 turnId 重新校准 seed（避免 seedTurnId 因后端规范化失效）
      const stillValid = mapped.some((m) => m.turnId === seedTurnId);
      setSelectedTurnIds(stillValid ? new Set([seedTurnId]) : new Set());
      setShareMode('selecting');
    } catch {
      // 错误提示由 request 拦截器统一处理
      setShareMode(null);
      setSelectedTurnIds(new Set());
    }
  };

  const handleToggleTurn = (turnId: string) => {
    setSelectedTurnIds((prev) => {
      const next = new Set(prev);
      if (next.has(turnId)) next.delete(turnId);
      else next.add(turnId);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    const all = turnSummary.turnIds.filter((tid) => turnSummary.turnSelectable[tid]);
    if (all.length === 0) return;
    const isAllSelected = all.every((tid) => selectedTurnIds.has(tid)) && selectedTurnIds.size === all.length;
    if (isAllSelected) {
      setSelectedTurnIds(new Set());
    } else {
      setSelectedTurnIds(new Set(all));
    }
  };

  const handleCancelShareMode = () => {
    setShareMode(null);
    setSelectedTurnIds(new Set());
    setShareDialogOpen(false);
  };

  const handleOpenShareDialog = () => {
    if (selectedTurnIds.size === 0) return;
    setShareDialogOpen(true);
  };

  /** 当前选中 turn 展开后的 messageIds（按 viewing.messages 排序） */
  const selectedMessageIds = useMemo(() => {
    if (!viewing) return [] as string[];
    return viewing.messages.filter((m) => selectedTurnIds.has(m.turnId)).map((m) => m.id);
  }, [viewing?.messages, selectedTurnIds]);

  /** 当前选中 turn 对应的完整消息（供生成图片复用） */
  const selectedMessages = useMemo(() => {
    if (!viewing) return [] as ChatMsg[];
    return viewing.messages.filter((m) => selectedTurnIds.has(m.turnId));
  }, [viewing?.messages, selectedTurnIds]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const resetInput = () => {
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    if (!files.length) return;

    if (uploadedFiles.length + files.length > MAX_FILE_COUNT) {
      message.warning(`最多上传 ${MAX_FILE_COUNT} 个文件`);
      resetInput();
      return;
    }
    const oversize = files.find((f) => f.size > MAX_FILE_SIZE);
    if (oversize) {
      message.warning(`文件 "${oversize.name}" 超过 10MB，请重新选择`);
      resetInput();
      return;
    }
    const invalid = files.find((f) => {
      const dot = f.name.lastIndexOf('.');
      const ext = dot > -1 ? f.name.slice(dot).toLowerCase() : '';
      return !ACCEPT_EXTS.includes(ext);
    });
    if (invalid) {
      message.warning(`不支持的文件格式："${invalid.name}"`);
      resetInput();
      return;
    }
    const emptyFile = files.find((f) => f.size === 0);
    if (emptyFile) {
      message.warning(`文件 "${emptyFile.name}" 内容为空，请重新选择`);
      resetInput();
      return;
    }

    setUploading(true);
    try {
      const results = await Promise.all(files.map((f) => _uploadFile(f)));
      setUploadedFiles((prev) => [...prev, ...results]);
    } catch {
      // 错误提示由 request 拦截器统一处理
    } finally {
      setUploading(false);
      resetInput();
    }
  };

  const removeFile = (idx: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // 顶部拖柄：向上拖增高，clamp 到 [96, min(360, 50vh)]
  const onInputResizeDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = inputHeight;
    const max = Math.min(360, Math.floor(window.innerHeight * 0.5));
    const move = (ev: MouseEvent) => {
      setInputHeight(Math.max(96, Math.min(max, startH + (startY - ev.clientY))));
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    document.body.style.userSelect = 'none';
  };

  // 左边缘拖柄：向左拖增宽，clamp 到 [360, min(900, 90vw)]
  const onWidthResizeDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = drawerWidth;
    const max = Math.min(900, Math.floor(window.innerWidth * 0.9));
    const move = (ev: MouseEvent) => {
      setDrawerWidth(Math.max(360, Math.min(max, startW + (startX - ev.clientX))));
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    document.body.style.userSelect = 'none';
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (shareMode === 'selecting') return;
    if (isSendKey(e, sendMode)) {
      e.preventDefault();
      handleSend(input);
    }
  };

  const historyItems: HistoryItem[] = useMemo(
    () =>
      sessions.map((s) => ({
        id: s.id,
        title: s.title,
        lastActive: s.lastActive,
        isViewing: s.id === viewingSessionId,
        deletable: !(s.id === viewingSessionId && sendInflight) && (s.source === 'local-new' || !!s.projectId),
        undeletableReason:
          s.id === viewingSessionId && sendInflight
            ? '消息正在发送中'
            : s.source === 'remote' && !s.projectId
            ? '会话未就绪'
            : undefined
      })),
    [sessions, viewingSessionId, sendInflight]
  );

  const newSessionDisabled =
    !!viewing && viewing.source === 'local-new' && viewing.messages.length === 0 && !isReplying && !sendInflight;

  if (!open) return null;

  return (
    <>
      {/* 抽屉 */}
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: drawerWidth,
          background: '#fff',
          zIndex: 1101,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.08)',
          animation: 'aiSlideIn 0.25s cubic-bezier(0.32,0.72,0,1)'
        }}
      >
        {/* 左边缘拖柄：拖拽改变面板宽度 */}
        <div
          onMouseDown={onWidthResizeDown}
          title="拖动调整面板宽度"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 6,
            cursor: 'ew-resize',
            zIndex: 1102
          }}
        />
        <SessionHeader
          title={viewing?.title || '新会话'}
          onNewSession={handleNewSession}
          onToggleHistory={() => {
            if (shareMode === 'selecting' || shareMode === 'loading') return;
            setHistoryPanelOpen((v) => !v);
          }}
          onClose={onClose}
          historyOpen={historyPanelOpen}
          newSessionDisabled={newSessionDisabled}
        />

        {historyPanelOpen && (
          <SessionHistoryPanel
            items={historyItems}
            onSelect={handleSwitchSession}
            onDelete={handleDeleteSession}
            onCloseOverlay={() => setHistoryPanelOpen(false)}
          />
        )}

        {/* 上下文条 */}
        {ctx.moduleId && (
          <div
            style={{
              padding: '10px 16px',
              background: '#EAF2FE',
              fontSize: 13,
              color: '#4B5563',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexShrink: 0
            }}
          >
            <span style={{ flexShrink: 0 }}>📍</span>
            <span style={{ flexShrink: 0 }}>当前页面：</span>
            <span
              style={{
                flex: 1,
                color: '#2A6FF5',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={ctx.breadcrumb?.join(' / ') || ctx.pageTitle}
            >
              {(ctx.breadcrumb?.length ? ctx.breadcrumb.join(' / ') : ctx.pageTitle) || ctx.moduleId}
            </span>
          </div>
        )}

        {/* 内容区（含历史面板悬浮） */}
        <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {shareMode === 'loading' && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: 'linear-gradient(90deg, #1677ff 30%, transparent)',
                animation: 'aiShareLoadingBar 1.2s linear infinite',
                zIndex: 11
              }}
            />
          )}
          {shareMode === 'selecting' && (
            <ShareSelectionBar
              count={selectedTurnIds.size}
              total={turnSummary.turnIds.filter((tid) => turnSummary.turnSelectable[tid]).length}
              onToggleSelectAll={handleToggleSelectAll}
              onShare={handleOpenShareDialog}
              onCancel={handleCancelShareMode}
            />
          )}
          <div
            ref={listRef}
            onScroll={handleListScroll}
            style={{
              flex: 1,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              padding: '20px 16px',
              background: '#FAFBFC'
            }}
          >
            {historyLoadingId === viewing?.id ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#8C8C8C', fontSize: 13 }}>
                <LoadingOutlined style={{ fontSize: 20, marginBottom: 8 }} />
                <div>加载历史消息...</div>
              </div>
            ) : !viewing || viewing.messages.length === 0 ? (
              <EmptyState suggestions={suggestions} onPick={(text) => handleSend(text)} />
            ) : (
              <>
                {shareMode === 'selecting' && <div style={{ height: 40 }} />}
                {viewing.messages.map((m, idx) => {
                  const inSelectMode = shareMode === 'selecting';
                  const selectable = inSelectMode && !!m.turnId && !m.turnId.startsWith('local_fail_');
                  const turnSelectableFlag = m.turnId ? turnSummary.turnSelectable[m.turnId] : false;
                  const isLastMsg = idx === viewing.messages.length - 1;
                  return (
                    <Message
                      key={m.id}
                      role={m.role}
                      content={m.content}
                      createTime={m.createTime}
                      thinking={m.thinking}
                      tokens={m.tokens}
                      questionData={m.questionData}
                      questionAnswers={m.questionAnswers}
                      questionDisabled={inSelectMode || !isLastMsg || !!m.questionAnswers}
                      onSubmitAnswer={
                        m.questionData ? (payload) => handleSubmitAnswer(viewing.id, m.id, payload) : undefined
                      }
                      selectable={selectable}
                      selected={selectable && selectedTurnIds.has(m.turnId)}
                      selectDisabled={selectable && !turnSelectableFlag}
                      onToggleSelect={selectable && turnSelectableFlag ? () => handleToggleTurn(m.turnId) : undefined}
                      onShare={
                        !inSelectMode && m.role === 'ai' && m.turnId && !m.turnId.startsWith('local_fail_')
                          ? () => enterShareMode(m.turnId)
                          : undefined
                      }
                      onToggleThinking={
                        m.thinking
                          ? () => {
                              setSessions((prev) =>
                                prev.map((s) =>
                                  s.id === viewingSessionId
                                    ? {
                                        ...s,
                                        messages: s.messages.map((mm) =>
                                          mm.id === m.id && mm.thinking
                                            ? { ...mm, thinking: { ...mm.thinking, expanded: !mm.thinking.expanded } }
                                            : mm
                                        )
                                      }
                                    : s
                                )
                              );
                            }
                          : undefined
                      }
                    />
                  );
                })}
              </>
            )}
          </div>
        </div>

        {/* 输入区 */}
        <div style={{ padding: '14px 16px 16px', flexShrink: 0 }}>
          {/* 已上传文件列表 */}
          {uploadedFiles.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
              {uploadedFiles.map((f, i) => (
                <div
                  key={i}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 7px',
                    background: '#f0f6ff',
                    border: '1px solid #bae0ff',
                    borderRadius: 5,
                    fontSize: 11,
                    color: '#1677ff',
                    maxWidth: 160
                  }}
                >
                  <svg viewBox="0 0 10 10" fill="none" width={9} height={9}>
                    <path
                      d="M6 1H3a.4.4 0 00-.4.4v7.2c0 .22.18.4.4.4h4a.4.4 0 00.4-.4V3L6 1z"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinejoin="round"
                    />
                    <path d="M6 1v2h2" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
                  </svg>
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 100
                    }}
                    title={f.name}
                  >
                    {f.name}
                  </span>
                  <span
                    onClick={() => removeFile(i)}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#ff4d4f')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#91caff')}
                    style={{
                      fontSize: 13,
                      lineHeight: 1,
                      cursor: 'pointer',
                      color: '#91caff',
                      flexShrink: 0
                    }}
                  >
                    ×
                  </span>
                </div>
              ))}
            </div>
          )}

          <SkillChipBar
            enabledIds={enabledSkillIds}
            detailMap={skillDetailMap}
            onToggleOff={handleToggleEnabledSkill}
          />

          {(() => {
            const stats = viewing?.stats;
            if (!stats || stats.totalTokenCount === undefined || stats.totalTokenCount <= 0) return null;
            return (
              <div style={{ marginBottom: 6 }}>
                <TokenTag
                  variant="session"
                  total={stats.totalTokenCount}
                  input={stats.totalInput}
                  output={stats.totalComplete}
                  cache={stats.totalCache}
                />
              </div>
            );
          })()}

          {/* 输入框容器 */}
          <div
            style={{
              background: '#fff',
              border: '1px solid #e5e6eb',
              borderRadius: 16,
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(29,33,41,0.06)',
              transition: 'border-color 0.2s, box-shadow 0.2s'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#1677ff';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(22,119,255,0.12), 0 10px 28px rgba(29,33,41,0.08)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#e5e6eb';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(29,33,41,0.06)';
            }}
          >
            <div
              onMouseDown={onInputResizeDown}
              style={{
                height: 11,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'ns-resize'
              }}
              title="拖动调整输入框高度"
            >
              <div style={{ width: 32, height: 3, borderRadius: 2, background: '#d9dde3' }} />
            </div>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={
                shareMode === 'selecting'
                  ? '分享中，无法发送消息'
                  : isReplying
                  ? 'AI 正在回复中...'
                  : SEND_MODE_PLACEHOLDER[sendMode]
              }
              rows={1}
              disabled={isReplying || shareMode === 'selecting'}
              style={{
                width: '100%',
                padding: '6px 16px 8px',
                border: 'none',
                outline: 'none',
                fontSize: 13,
                fontFamily: 'inherit',
                color: '#1d2129',
                resize: 'none',
                background: 'transparent',
                lineHeight: 1.7,
                display: 'block',
                height: inputHeight,
                overflowY: 'auto',
                overscrollBehavior: 'contain'
              }}
            />
            {/* 内嵌底部工具条 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '4px 12px 12px 14px'
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                multiple
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div
                  onClick={() =>
                    shareMode !== 'selecting' &&
                    !uploading &&
                    !isReplying &&
                    uploadedFiles.length < MAX_FILE_COUNT &&
                    fileInputRef.current?.click()
                  }
                  onMouseEnter={(e) => {
                    if (!uploading && !isReplying && uploadedFiles.length < MAX_FILE_COUNT) {
                      e.currentTarget.style.background = '#f2f3f5';
                      e.currentTarget.style.color = '#4e5969';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = uploading ? '#1677ff' : '#8a94a6';
                  }}
                  title={
                    isReplying
                      ? '回复中,请稍候'
                      : uploadedFiles.length >= MAX_FILE_COUNT
                      ? `最多 ${MAX_FILE_COUNT} 个文件`
                      : '上传附件（图片 / PDF / Word / Excel / TXT / Markdown，单个 ≤ 10MB）'
                  }
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor:
                      uploading || isReplying || uploadedFiles.length >= MAX_FILE_COUNT ? 'not-allowed' : 'pointer',
                    color: uploading ? '#1677ff' : '#8a94a6',
                    opacity: isReplying ? 0.5 : 1,
                    transition: 'all 0.15s'
                  }}
                >
                  {uploading ? (
                    <LoadingOutlined style={{ fontSize: 16 }} />
                  ) : (
                    <PaperClipOutlined style={{ fontSize: 16 }} />
                  )}
                </div>
                {configLoaded && providers.length > 0 && (
                  <ModelPicker
                    value={currentModelId}
                    providers={providers}
                    onChange={handleModelChange}
                    disabled={isReplying || shareMode === 'selecting'}
                  />
                )}
                {configLoaded && (
                  <div ref={skillAnchorRef} style={{ position: 'relative' }}>
                    <SkillToolButton
                      active={popoverOpen}
                      disabled={isReplying || shareMode === 'selecting'}
                      onClick={() => setPopoverOpen((v) => !v)}
                    />
                    <SkillPopover
                      open={popoverOpen}
                      sharedIds={sharedSkillIds}
                      personalIds={selectedSkillIds}
                      enabledIds={enabledSkillIds}
                      detailMap={skillDetailMap}
                      anchorRef={skillAnchorRef}
                      onOpenDialog={() => {
                        setPopoverOpen(false);
                        setDialogOpen(true);
                      }}
                      onToggleEnabled={handleToggleEnabledSkill}
                      onRemoveFromConfig={handleRemoveSkill}
                      onClose={() => setPopoverOpen(false)}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <SendModeSelector
                  sendMode={sendMode}
                  onChange={setSendMode}
                  open={sendModeMenuOpen}
                  onOpenChange={setSendModeMenuOpen}
                />

                {isReplying ? (
                  <button
                    type="button"
                    onClick={handleInterrupt}
                    title="停止生成"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: '#1d2129',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#4e5969')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#1d2129')}
                  >
                    <svg viewBox="0 0 14 14" fill="none" width={12} height={12}>
                      <rect x="3" y="3" width="8" height="8" rx="1.5" fill="white" />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSend(input)}
                    disabled={!input.trim() || uploading || shareMode === 'selecting'}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: input.trim() && !uploading && shareMode !== 'selecting' ? '#1677ff' : '#c8cfd8',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: input.trim() && !uploading && shareMode !== 'selecting' ? 'pointer' : 'not-allowed',
                      boxShadow:
                        input.trim() && !uploading && shareMode !== 'selecting'
                          ? '0 6px 14px rgba(22,119,255,0.28)'
                          : 'none',
                      transition: 'all 0.15s'
                    }}
                  >
                    <svg viewBox="0 0 14 14" fill="none" width={12} height={12}>
                      <path
                        d="M12 2L6.5 7.5M12 2L8.5 12l-2-4.5L2 5.5 12 2Z"
                        stroke="white"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              color: '#9CA3AF',
              textAlign: 'center'
            }}
          >
            对话关闭后自动保存到「历史对话」
          </div>
        </div>
      </div>
      <style>{`
        @keyframes aiFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes aiSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes aiShareLoadingBar {
          0% { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }
      `}</style>
      <SkillsDialog
        open={dialogOpen}
        selectedIds={selectedSkillIds}
        categories={skillCategories}
        loaders={skillLoaders}
        submitting={skillSubmitting}
        onConfirm={handleConfirmSkills}
        onClose={() => setDialogOpen(false)}
      />
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        projectId={viewing?.projectId || ''}
        selectedMessageIds={selectedMessageIds}
        selectedMessages={selectedMessages}
      />
    </>
  );
}
